import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { opdrachtenAPI, employerOpdrachtenAPI } from '../services/api';
import toast from 'react-hot-toast';

export type OpdrachtStatus = 'nieuw' | 'geaccepteerd' | 'onderhandelingen' | 'afgerond' | 'afgewezen';

export interface Opdracht {
  id: string;
  vacancy_id: string;
  professional_id?: string;
  titel: string;
  beschrijving: string;
  professional_type: string;
  employer_company_name?: string;
  employer_logo?: string;
  application_status: OpdrachtStatus;
  is_negotiating?: boolean;
  proposed_changes?: any;
  change_proposed_by?: string;
  change_approved_by_professional?: boolean;
  change_approved_by_employer?: boolean;
  completion_requested?: boolean;
  completion_requested_by?: string;
  completion_approved_by_professional?: boolean;
  completion_approved_by_employer?: boolean;
  rejection_requested?: boolean;
  rejection_requested_by?: string;
  rejection_reason?: string;
  rejection_approved_by_professional?: boolean;
  rejection_approved_by_employer?: boolean;
  professional_first_name?: string;
  professional_last_name?: string;
  professional_email?: string;
  [key: string]: any;
}

// Professional hooks
export const useOpdrachten = (status?: OpdrachtStatus) => {
  return useQuery<Opdracht[]>({
    queryKey: ['opdrachten', 'professional', status],
    queryFn: async () => {
      const res = await opdrachtenAPI.getByStatus(status);
      return res.data.data || [];
    },
  });
};

export const useNegotiateOpdracht = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: any }) =>
      opdrachtenAPI.negotiate(id, changes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten'] });
      toast.success('Wijzigingen voorgesteld');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij voorstellen van wijzigingen');
    },
  });
};

export const useApproveChange = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => opdrachtenAPI.approveChange(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten'] });
      toast.success('Wijziging goedgekeurd');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij goedkeuren van wijziging');
    },
  });
};

export const useCompleteOpdracht = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => opdrachtenAPI.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten'] });
      toast.success('Afronding aangevraagd');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij aanvragen van afronding');
    },
  });
};

export const useApproveCompletion = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => opdrachtenAPI.approveCompletion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten'] });
      toast.success('Afronding goedgekeurd');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij goedkeuren van afronding');
    },
  });
};

export const useRejectOpdracht = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      opdrachtenAPI.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten'] });
      toast.success('Afwijzing aangevraagd');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij aanvragen van afwijzing');
    },
  });
};

export const useApproveRejection = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => opdrachtenAPI.approveRejection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten'] });
      toast.success('Afwijzing goedgekeurd');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij goedkeuren van afwijzing');
    },
  });
};

// Employer hooks
export const useEmployerOpdrachten = (status?: OpdrachtStatus) => {
  return useQuery<Opdracht[]>({
    queryKey: ['opdrachten', 'employer', status],
    queryFn: async () => {
      const res = await employerOpdrachtenAPI.getByStatus(status);
      return res.data.data || [];
    },
  });
};

export const useEmployerNegotiateOpdracht = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, professionalId, changes }: { id: string; professionalId: string; changes: any }) =>
      employerOpdrachtenAPI.negotiate(id, professionalId, changes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten', 'employer'] });
      toast.success('Wijzigingen voorgesteld');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij voorstellen van wijzigingen');
    },
  });
};

export const useEmployerApproveChange = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, professionalId }: { id: string; professionalId: string }) =>
      employerOpdrachtenAPI.approveChange(id, professionalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten', 'employer'] });
      toast.success('Wijziging goedgekeurd');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij goedkeuren van wijziging');
    },
  });
};

export const useEmployerCompleteOpdracht = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, professionalId }: { id: string; professionalId: string }) =>
      employerOpdrachtenAPI.complete(id, professionalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten', 'employer'] });
      toast.success('Afronding aangevraagd');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij aanvragen van afronding');
    },
  });
};

export const useEmployerApproveCompletion = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, professionalId }: { id: string; professionalId: string }) =>
      employerOpdrachtenAPI.approveCompletion(id, professionalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten', 'employer'] });
      toast.success('Afronding goedgekeurd');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij goedkeuren van afronding');
    },
  });
};

export const useEmployerRejectOpdracht = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, professionalId, reason }: { id: string; professionalId: string; reason?: string }) =>
      employerOpdrachtenAPI.reject(id, professionalId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten', 'employer'] });
      toast.success('Afwijzing aangevraagd');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij aanvragen van afwijzing');
    },
  });
};

export const useEmployerApproveRejection = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, professionalId }: { id: string; professionalId: string }) =>
      employerOpdrachtenAPI.approveRejection(id, professionalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opdrachten', 'employer'] });
      toast.success('Afwijzing goedgekeurd');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij goedkeuren van afwijzing');
    },
  });
};
